import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  CardBody,
  CardTitle, Col, FormGroup,
  Input, InputGroup,
  Label,
  ListGroup, ListGroupItem,
  Modal,
  ModalBody, ModalFooter,
  ModalHeader, Row
} from "reactstrap";
import { useVsiAppContext } from "../../stores/VsiApp";
import { useDataWithRoute, useAuthWithRoute, useTitleFromComponent } from "../../utils/hooks";
import {
  booleanFormatter,
  formatCollectionDay,
  ipAddressFormatter,
  passwordRemovedFormatter,
  uploadStatusFormatter
} from "../../utils/gridFormatters";
import Grid from "../../components/Grid";
import Toggle from "react-toggle";
import { useNavigate } from "react-router-dom";
import HitachiInputs from "./CollectionDeviceInputs/HitachiInputs";
import EcsInputs from "./CollectionDeviceInputs/EcsInputs";
import OracleDatabaseInputs from "./CollectionDeviceInputs/OracleDatabaseInputs";
import Db2DatabaseInputs from "./CollectionDeviceInputs/Db2DatabaseInputs";
import GoogleCloudInputs from "./CollectionDeviceInputs/GoogleCloudInputs";
import PrometheusInputs from "./CollectionDeviceInputs/PrometheusInputs";
import BrocadeInputs from "./CollectionDeviceInputs/BrocadeInputs";
import RubrikRscInputs from "./CollectionDeviceInputs/RubrikRscInputs";
import Nouns from "./CollectionDeviceInputs/Nouns";
import CollectionDeviceInstructions from "../../components/CollectionDeviceInstructions";
import { DeleteAuth, PutAuth } from "../../utils/Verbs";
import { VsiBubble, VsiH4, VsiHr, VsiLgText, VsiSpanText } from "../../styled/App.styled";
import themeStyles, { vsiGreen } from "../../utils/themeStyles";
import VsiLabeledSelect from "../../components/VsiLabeledSelect";

import { validateIp } from "../../utils/Helpers";
import ThreeParInputs from "./CollectionDeviceInputs/ThreeParInputs";
import CohesityInputs from "./CollectionDeviceInputs/CohesityInputs";
import dayjs from "dayjs";

const dayOrder = ["sun", "mon", "tue", "wed", "thurs", "fri", "sat"];

const uploadDefs = [
  {
    field: "_id",
    headerName: "Upload ID",
    hide: true
  },
  {
    field: "originalFileName",
    headerName: "Original File",
    resizable: true
  },
  {
    field: "stampedFileName",
    headerName: "Stamped File",
    resizable: true
  },
  {
    field: "uploadedBy",
    headerName: "Uploaded By",
    resizable: true
  },
  {
    field: "deviceType",
    headerName: "Type",
    resizable: true
  },
  {
    field: "uploadDate",
    headerName: "Upload Date",
    resizable: true
  },
  {
    field: "expireAt",
    headerName: "Expires",
    resizable: true
  },

  {
    field: "fileSize",
    headerName: "File Size (MB)",
    resizable: true
  },
  {
    field: "parsingStatus",
    headerName: "Status",
    resizable: true,
    cellRenderer: uploadStatusFormatter
  },
  {
    field: "parseAttempts",
    headerName: "Parse Failures"
  }
];

const collectionDevicesDefs = [
  {
    field: "collection_device_id",
    width: 50,
    headerName: "ID",
    cellStyle: { textAlign: "center" },
    resizable: true,
    sortable: true,
    hide: true
  },
  {
    field: "_id",
    headerName: "Internal ID",
    hide: true
  },
  {
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 10,
    headerCheckboxSelectionFilteredOnly: true
  },
  {
    field: "device_type_code",
    flex: 1,
    minWidth: 100,
    headerName: "Device Type",
    resizable: true,
    sortable: true
  },
  {
    field: "ip_address",
    flex: 1,
    minWidth: 100,
    maxWidth: 200,
    headerName: "IP Address/DNS Entry",
    cellStyle: { textAlign: "center" },
    resizable: true,
    sortable: true,
    cellRenderer: ipAddressFormatter
  },
  {
    field: "device_alias",
    flex: 1,
    minWidth: 100,
    headerName: "Device Alias",
    sortable: true,
    resizable: true
  },
  {
    field: "collection_day",
    width: 200,
    headerName: "Collection Day(s)",
    sortable: true,
    resizable: true,
    cellStyle: { textAlign: "center" },
    cellRenderer: formatCollectionDay
  },
  {
    field: "collection_time",
    width: 150,
    headerName: "Collection Time",
    sortable: true,
    resizable: true,
    cellStyle: { textAlign: "center" }
  },
  {
    field: "site_code",
    flex: 1,
    minWidth: 100,
    headerName: "Collection Machine",
    sortable: true,
    resizable: true
  },
  {
    field: "password",
    width: 160,
    headerName: "Password Removed",
    valueFormatter: passwordRemovedFormatter,
    cellStyle: function(params) {
      if (!params.value || params.value.length === 0) return { textAlign: "center", color: vsiGreen };
      else return { textAlign: "center", color: "red" };
    },
    resizable: true,
    sortable: true,
    enableRowGroup: false,  // group by pass will reveal pass
    useValueFormatterForExport: true
  },
  {
    field: "enabled",
    headerName: "Enabled",
    width: 100,
    resizable: true,
    sortable: true,
    cellStyle: { textAlign: "center" },
    cellRenderer: booleanFormatter
  },
  {
    field: "report_rtm_data",
    headerName: "RTM",
    resizable: true,
    sortable: true,
    hide: true,
    cellStyle: { textAlign: "center" },
    cellRenderer: booleanFormatter
  }
];

const refreshRates = [{
    text: `Low (24h)`,
    value: 1440
  }, {
    text: `Medium (4h)`,
    value: 240
  }, {
    text: `High (15m)`,
    value: 15
  }];

export default function CollectionDevices({ title }) {
  useTitleFromComponent(title);

  const navigate = useNavigate();
  const { theme, client, setUser, getAccessTokenSilently } = useVsiAppContext();
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [changeIp, setChangeIp] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [changeCollectionMachine, setChangeCollectionMachine] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage] = useState("");
  const [alertColor] = useState("yellow");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [modalMode, setModalMode] = useState("edit");
  const [showUploadsModal, setShowUploadsModal] = useState(false);
  const [showFileAnalysisModal, setShowFileAnalysisModal] = useState(false);
  const [usePassword, setUsePassword] = useState(true);
  const [toggles, setToggles] = useState({
    autoUpload: client.autoUpload,
    downloadSchedule: client.downloadSchedule,
    autoUpdate: client.autoUpdate,
    enableLogging: client.enableLogging,
    refreshRate: client.refreshRate,
  });


  const [collectionDevicesData, { reFetchData: refetchCollectionDevices }] = useAuthWithRoute(`/api/v6/admin/collection_devices/table_data/${client.clientId}`);
  const [deviceTypes] = useAuthWithRoute(`/api/v6/admin/device_types`);
  const [uploads] = useDataWithRoute(`/api/v6/admin/uploads/${client.clientId}`);

  const deviceTypesList = useMemo(() => {
    if (deviceTypes.length) {
      return deviceTypes.map(dt => {
        return { label: dt.device_type_code.trim(), value: dt.device_type_code.trim() };
      });
    }
    return [];
  }, [deviceTypes]);

  const collectionDevices = useMemo(() => {
    return collectionDevicesData?.data ?? [];
  }, [collectionDevicesData]);


  const populateDays = (values) => {
    let selected_days = [];
    values?.forEach(v => {
      Nouns.days.forEach(d => {
        if (d.value === v) {
          selected_days.push(d);
        }
      });
    });
    setSelectedDays(selected_days);
  };
  const handleToggleUsePasword = () => {
    setUsePassword(!usePassword);
  };
  const handleToggleChange = (event) => {
    const { name } = event.target;
    const value = !toggles[name];
    const newToggles = {...toggles};
    newToggles[name] = value;
    setToggles(newToggles);
    editClientProp(name, value);
  };

  const editClientProp = async (name, value) => {
    let clientProp = {};
    clientProp[name] = (value);
    const token = await getAccessTokenSilently();
    PutAuth(`/api/v6/admin/clients/${client.clientId}`, clientProp, token)
      .then(data => {
        if (data.status === 200) {
          setUser(user => {
            return {
              ...user,
              client: {
                ...client,
                [name]: value
              }
            };
          });
        } else {
          console.error(data);
        }
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleRefreshRateChange = (event) => {
    const value = event.target.value;
    const newToggles = {...toggles};
    newToggles.refreshRate = value;
    setToggles(newToggles);
    editClientProp("refreshRate", value);
  };
  const toggleUploadsModal = () => {
    setShowUploadsModal(!showUploadsModal);
  };
  const handleUpdateSelectedClick = () => {
    if (selectedRows.length > 1) {
      setModalMode("Edit Multiple");

      setSelectedDevice({
        site_code: "",
        enabled: true,
        collection_day: "",
        collection_time: "",
        username: ""
      });
      populateDays([]);
      toggleDeviceModal();
    } else if (selectedRows.length === 1) {
      selectedRows[0].confirmPassword = selectedRows[0].password;
      setSelectedDevice(selectedRows[0]);
      populateDays(selectedRows[0].collection_day.split(","));
      setModalMode("Edit");
      if (!Nouns.FileAnalysisDevice.includes(selectedRows[0].device_type_code)) {
        toggleDeviceModal();
      } else {
        toggleFileAnalysisModal();
      }
    }
  };
  const toggleDeleteAllModal = () => {
    setShowDeleteAllModal(!showDeleteAllModal);
  };
  const validateForm = () => {
    return (
      selectedDevice?.password === selectedDevice?.confirmPassword &&
      (!selectedDevice?.ip_address || validateIp(selectedDevice?.ip_address))
    );
  };
  const handleMultiSelectChange = selectedItems => {
    selectedDevice.collection_day = selectedItems?.map(i => {
      return i.value;
    }).join(",");
    setSelectedDays(selectedItems);
  };
  const handleSelectChangeDTC = (event) => {
    setSelectedDevice({ ...selectedDevice, device_type_code: event.value });
  };
  const handleInputChange = (event) => {
    let { name, value } = event.target;
    if (name === "report_rtm_data" && typeof value !== "boolean") {
      value = value !== "true";
    }
    setSelectedDevice({ ...selectedDevice, [name]: value });
  };
  const toggleDeviceModal = () => {
    setChangePassword(false);
    setChangeIp(false);
    setChangeCollectionMachine(false);
    setUsePassword(!(selectedRows[0]?.ssh_key && selectedRows[0]?.ssh_key?.length > 8));
    setShowDeviceModal(!showDeviceModal);
  };

  const rowDoubleClicked = (event) => {
    event.data.confirmPassword = event.data.password;
    setSelectedDevice(event.data);
    populateDays(event.data.collection_day.split(","));
    setModalMode("Edit");
    if (!Nouns.FileAnalysisDevice.includes(event.data.device_type_code)) {
      toggleDeviceModal();
    } else {
      toggleFileAnalysisModal();
    }

  };
  const toggleFileAnalysisModal = () => {
    setShowFileAnalysisModal(!showFileAnalysisModal);
  };

  const changeIpBtn = () => {
    setChangeIp(!changeIp);
  };
  const changePasswordBtn = () => {
    setChangePassword(!changePassword);
  };
  const changeCollectionMachineBtn = () => {
    setChangeCollectionMachine(!changeCollectionMachine);
  };

  function CollectionMachineLabel(isChangeMachine) {
    return (
      <>
        <Label style={{ fontSize: isChangeMachine ? "0.7rem" : "initial" }} htmlFor="site_code">
          {isChangeMachine ?
            "Specifies which machine runs this task. Leave blank unless using multiple machines."
            : "Collection Machine (Optional)"} </Label>
        <Button className="mb-4" color="secondary" type="button"
                onClick={changeCollectionMachineBtn}>
          {changeCollectionMachine ? "Cancel Change" : "Click to Change"}
        </Button>
      </>
    );
  }

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };
  const handleDeviceDelete = async () => {
    const IDsToBeDeleted = selectedRows.map(row => {
      return row._id;
    });
    const token = await getAccessTokenSilently();
    IDsToBeDeleted.forEach(id => {
      DeleteAuth(`/api/v6/admin/collection_devices/${id}`, token).then(() => {
        refetchCollectionDevices();
      });
    });

    setShowDeleteModal(false);
    setShowDeleteAllModal(false);
    setShowDeviceModal(false);
    setShowFileAnalysisModal(false);
  };
  const toggleWarningModal = () => {
    setShowWarningModal(!showWarningModal);
  };
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };
  const updateDevicesHandler = async () => {
    // Cleaning up object without values
    let fieldsToBeUpdated = { clientId: client.clientId };
    if (modalMode === "Edit Multiple") {
      Object.keys(selectedDevice).forEach(k => {
        if (selectedDevice[k] !== "") {
          fieldsToBeUpdated[k] = selectedDevice[k];
        }
      });
    } else {
      fieldsToBeUpdated = selectedDevice;
    }

    const token = await getAccessTokenSilently();
    selectedRows?.forEach(row => {
      PutAuth(`/api/v6/admin/collection_devices/${row["_id"]}`, fieldsToBeUpdated, token).then(() => {
        refetchCollectionDevices();
      });
    });
    setSelectedRows([]);
    setShowDeviceModal(false);
    setShowFileAnalysisModal(false);
  };
  const createDate = (dayStr, time) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    const inputDay = dayOrder.indexOf(dayStr);
    const currentDay = date.getDay();
    const calenderDayDate = date.getDate() + (inputDay - currentDay);
    const hours = time.slice(0, 2);
    const minutes = time.slice(3);

    return new Date(year, month, calenderDayDate, hours, minutes, 0, 0);
  };
  const getNextUploads = () => {
    if (collectionDevices.length > 0) {
      let smallestTimeDifference = 1e12;
      let upNextCollections = [];

      collectionDevices.forEach(device => {
        let collectionDayArr = device.collection_day.split(",");
        let date = new Date();

        collectionDayArr.forEach((day) => {
          let collectionTimeStamp = createDate(day, device.collection_time);
          let timeDifference = collectionTimeStamp - date;

          if (timeDifference >= 0 && timeDifference < smallestTimeDifference) {
            upNextCollections = [];
            smallestTimeDifference = timeDifference;
            upNextCollections.push(device);
          } else if (timeDifference >= 0 && timeDifference === smallestTimeDifference) {
            upNextCollections.push(device);
          }
        });
      });

      return upNextCollections;
    }
  };

  const onRowSelected = (event) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const handleAddDeviceClick = () => {
    navigate("/user/settings/collection-devices/add-collection-device");
  };

  return (
    <div>
      <VsiH4>Collection Devices</VsiH4>
      <VsiHr />
      <VsiBubble className={"mb-2"} noAutobot>
        <CardBody style={{ padding: 0 }}>
          <div className="config-container mt-4 mb-4 d-flex flex-row justify-content-around">
            <div className="d-flex align-items-center flex-column ">
              <label htmlFor="auto-upload" className="config-label mb-0 mr-2">
                <VsiLgText>
                  Auto Upload
                </VsiLgText></label>
              <Toggle id="auto-upload" name="autoUpload"
                      checked={toggles.autoUpload}
                      onChange={handleToggleChange} />
            </div>
            <div className="d-flex align-items-center flex-column justify-content-center">
              <label htmlFor="download-schedule" className="config-label mb-0 mr-2">
                <VsiLgText>
                  Download Schedule
                </VsiLgText></label>
              <Toggle id="download-schedule" name="downloadSchedule"
                      checked={toggles.downloadSchedule}
                      onChange={handleToggleChange} />
            </div>
            <div className="d-flex align-items-center flex-column justify-content-center">
              <label htmlFor="auto-update" className="config-label mb-0 mr-2">
                <VsiLgText>
                  Auto Update
                </VsiLgText></label>
              <Toggle id="auto-update" name="autoUpdate"
                      checked={toggles.autoUpdate}
                      onChange={handleToggleChange} />
            </div>
            <div className="d-flex align-items-center flex-column justify-content-center">
              <label htmlFor="enable-logging" className="config-label mb-0 mr-2">
                <VsiLgText>
                  Enable Logging
                </VsiLgText></label>
              <Toggle id="enable-logging" name="enableLogging"
                      checked={toggles.enableLogging}
                      onChange={handleToggleChange} />
            </div>
            <div className="d-flex align-items-center flex-column justify-content-center">
              <label htmlFor="refresh-rate" className="config-label mb-0 mr-2">
                <VsiLgText>
                  Refresh Rate
                </VsiLgText></label>
              <Input type="select" name="select" id="refreshRate"
                     onChange={handleRefreshRateChange}
                     value={toggles.refreshRate}>
                {refreshRates.map((rate, i) =>
                  <option key={`rate-${rate.text}-${i}`} value={rate.value}>{rate.text}</option>)}
              </Input>
            </div>
          </div>
        </CardBody>
      </VsiBubble>

      <VsiBubble className={"mb-2"} noAutobot>
        <CardBody>
          <div className="config-container ms-4 mt-2 mb-4 d-flex flex-row">
            <VsiLgText>
              Last VSI Client
              Session: {client.lastVsiClientSession ? dayjs(client.lastVsiClientSession).tz("America/Chicago").format("MM/DD/YYYY h:mm A") : "N/A"}
            </VsiLgText>
          </div>
        </CardBody>
      </VsiBubble>

      <div>
        <div style={{ color: "lightgrey" }}>Double Click on a device in the table to update.</div>
        <div style={{ display: "flex" }}>
          <Button className={"my-2 mr-1"} color="success" onClick={handleAddDeviceClick}
                  style={{ marginRight: 5 }}>Add Device</Button>
          <Button className={"my-2 mr-1"} color="info" onClick={handleUpdateSelectedClick}
                  disabled={selectedRows.length < 1} style={{ marginRight: 5 }}>Update Selected</Button>
          <Button className={"my-2"} color="danger" onClick={() => {
            setShowDeleteAllModal(true);
          }} disabled={selectedRows.length < 1} style={{ marginRight: 5 }}>Delete Selected</Button>
          <Button onClick={toggleUploadsModal} color="secondary" className="my-2"
                  style={{ marginLeft: "auto" }}>Latest Uploads</Button>
          <Button onClick={toggleInstructions} color="warning" className="my-2"
                  style={{ marginLeft: 5 }}>Installation
            Instructions</Button>
          <CollectionDeviceInstructions showModal={showInstructions}
                                        toggle={toggleInstructions} />
        </div>

        <Modal className="delete-modal" backdrop="static"
               isOpen={showDeleteAllModal}>
          <ModalHeader className={`bg-${theme}`} toggle={toggleDeleteAllModal}>
            <VsiSpanText>
              Are you sure you want to
              delete these devices? <b>This action is irreversible.</b>
            </VsiSpanText>
          </ModalHeader>
          <ModalBody className={`bg-${theme}`}>
            <ListGroup className={`bg-${theme}`}>
              {selectedRows?.map((device, index) => (
                <ListGroupItem style={{ color: themeStyles[theme].textColor }} className={`bg-${theme}`}
                               key={`list-group-item-delete-${device.device_type_code}-${index}`}>
                  {`${device.device_type_code} - ${device.device_alias}`}
                </ListGroupItem>
              ))}
            </ListGroup>
          </ModalBody>
          <ModalFooter className={`bg-${theme}`}>
            <Button color="danger" onClick={handleDeviceDelete}>Yes</Button>
            <Button color="secondary" onClick={toggleDeleteAllModal}>No</Button>
          </ModalFooter>
        </Modal>
      </div>
      {collectionDevices && collectionDevices.length > 0 ? (<>
          <div className="ag-theme-fresh ag-grid-container">
            <Grid data={collectionDevicesData.data}
                  columnDefs={collectionDevicesDefs}
                  rowSelection={"multiple"}
                  onRowSelected={onRowSelected}
                  rowDoubleClicked={rowDoubleClicked} />
          </div>
          <div className="ag-theme-fresh ag-grid-container"
               style={{ marginTop: 100, backgroundColor: "lightgrey" }}>
            <CardTitle tag="h2" className="text-center">Up Next</CardTitle>
            <Grid
              columnDefs={collectionDevicesDefs} hideGridToolbar={true}
              data={getNextUploads()} />
          </div>
        </>
      ) : (<>
          <h3 className="text-center" style={{ color: "lightgrey" }}>It looks like you have no collection
            devices. Click add
            device to create a new collection device.</h3>

        </>
      )}

      <Modal backdrop="static" isOpen={showDeviceModal} size="lg">
        <ModalHeader toggle={toggleDeviceModal} className={`bg-${theme}`}>
          <VsiH4>{modalMode} Device{(selectedRows.length > 1) ? "s" : ""}</VsiH4>
          {(selectedRows.length > 1) ? (
            <span
              id={"update-devices-span-info"}>If you do NOT want a setting changed, leave the input field <strong>blank</strong></span>
          ) : null}
        </ModalHeader>
        <ModalBody className={`bg-${theme}`}>
          <Row>
            <Col sm={6} xs={12}>
              <Label htmlFor="device_type_code">Device Type</Label>
              <VsiLabeledSelect
                options={deviceTypesList}
                minWidth={"100%"}
                defaultValue={{
                  label: selectedDevice?.device_type_code?.trim(),
                  value: selectedDevice?.device_type_code?.trim()
                } || {}}
                isSearchable
                name="device_type_code"
                onChange={handleSelectChangeDTC}
              />
            </Col>
          </Row>

          <Row>
            {selectedRows.length < 2 ?
              <Col sm={4} xs={12}>
                <Label htmlFor="device_alias">Device Alias</Label>
                <Input type="text" value={selectedDevice?.device_alias}
                       name="device_alias" onChange={handleInputChange} />
              </Col> : null}
            {(changeCollectionMachine || selectedRows.length > 1) ?
              <Col sm={selectedRows.length < 2 ? 4 : 12} xs={12}>
                <Label htmlFor="site_code">Collection Machine (Optional)</Label>
                <Input type="text" name="site_code" value={selectedDevice?.site_code}
                       onChange={handleInputChange} />
              </Col> : null}
            {selectedRows.length < 2 ?
              <Col sm={4} xs={12}
                   style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                {CollectionMachineLabel(changeCollectionMachine)}
              </Col> : null}
            {
              (selectedDevice?.device_type_code?.includes?.("COMPELLENT") || selectedDevice?.device_type_code?.includes?.("XTREMIO")) &&
              selectedRows.length < 2 ? (
                <Col sm={4} xs={12}>
                  <Label htmlFor="array_name">Array Name</Label>
                  <Input type="text" value={selectedDevice?.array_name}
                         name="array_name" onChange={handleInputChange} />
                </Col>
              ) : null}
            {
              (selectedDevice?.device_type_code?.includes?.("ORACLE_EXADATA")) &&
              selectedRows.length < 2 ? (
                <Col sm={4} xs={12}>
                  <Label htmlFor="secondary_ip">Cells</Label>
                  <Input type="text" value={selectedDevice?.secondary_ip}
                         name="secondary_ip" onChange={handleInputChange} />
                </Col>
              ) : null}
          </Row>
          {selectedRows.length < 2 ?
            <Row>
              {Nouns.cliDevices.includes(selectedRows?.[0]?.device_type_code) ?
                (<Col xs={12} lg={8}>
                  <Label htmlFor="cli_path">CLI Path</Label>
                  <Input type="text" value={selectedDevice.cli_path}
                         name="cli_path" onChange={handleInputChange} />
                </Col>) : null}
              {Nouns.portDevices.some(portDevice => selectedRows?.[0]?.device_type_code.includes(portDevice)) ? (
                <Col lg={4} sm={6}>
                  <Label htmlFor="port">Port</Label>
                  <Input required type="text" name="port" value={selectedDevice.port}
                         onChange={handleInputChange} />
                </Col>
              ) : null}
            </Row> : null}

          <Row>
            <Col sm={6} xs={12}>
              <Label htmlFor="collection_day">Collection Day(s)</Label>
              <VsiLabeledSelect
                defaultValue={selectedDays}
                minWidth={"100%"}
                options={[
                  { value: "mon", label: "Monday" },
                  { value: "tue", label: "Tuesday" },
                  { value: "wed", label: "Wednesday" },
                  { value: "thurs", label: "Thursday" },
                  { value: "fri", label: "Friday" },
                  { value: "sat", label: "Saturday" },
                  { value: "sun", label: "Sunday" }
                ]} name="collection_day"
                isMulti
                onChange={handleMultiSelectChange}
              />
            </Col>
            <Col sm={6} xs={12}>
              <Label htmlFor="collectionTime">Collection Time</Label>
              <Input style={{ background: "rgb(250, 250, 250)," }}
                     id={"collection_time"}
                     type="time"
                     bsSize="small"
                     className={"form-control"}
                     required={true}
                     name="collection_time"
                     value={selectedDevice?.collection_time}
                     onChange={handleInputChange}
                     step={300}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={6} xs={12}>
              <Label htmlFor="username">Username</Label>
              <Input type="text" name="username" value={selectedDevice?.username}
                     onChange={handleInputChange} />
            </Col>
            <Col sm={3} xs={12}>
              <Label htmlFor="device-enabled" style={{ marginRight: 10 }}>Enabled</Label><br />
              <Toggle id="device-enabled" name="enabled"
                      checked={selectedDevice?.enabled}
                      onChange={() => {
                        setSelectedDevice({ ...selectedDevice, enabled: !selectedDevice?.enabled });
                      }} />
            </Col>
            {selectedRows.length < 2 ?
              <Col sm={3} xs={12}>
                <Label htmlFor="timeout">Collection Timeout (hrs)</Label>

                <Input value={selectedDevice?.timeout}
                       name="timeout"
                       type="number"
                       onChange={handleInputChange} />
              </Col> : null}
          </Row>
          {selectedRows.length < 2 ? (
            <Row>
              <BrocadeInputs
                device={selectedDevice?.device_type_code}
                array_name={selectedDevice?.array_name}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <CohesityInputs
              device={selectedDevice?.device_type_code}
              domain={selectedDevice?.domain}
              apiToken={selectedDevice?.api_token}
              handleInputChange={handleInputChange}
              lgColSize={6}
            />
          ) : null}
          {selectedRows.length < 2 && selectedDevice?.device_type_code?.includes?.("ECS_DEVICE") ? (
            <Row>
              <EcsInputs
                device={selectedDevice?.device_type_code}
                port={selectedDevice?.port}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 && selectedDevice?.device_type_code?.includes?.("ORACLE_DATABASE") ? (
            <Row>
              <OracleDatabaseInputs
                device={selectedDevice?.device_type_code}
                port={selectedDevice?.port}
                array_name={selectedDevice?.array_name}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <Row>
              <Db2DatabaseInputs
                device={selectedDevice?.device_type_code}
                port={selectedDevice?.port}
                array_name={selectedDevice?.array_name}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <Row>
              <GoogleCloudInputs
                device={selectedDevice?.device_type_code}
                cli_path={selectedDevice?.cli_path}
                domain={selectedDevice?.domain}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <Row>
              <PrometheusInputs
                device={selectedDevice?.device_type_code}
                port={selectedDevice?.port}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <Row>
              <RubrikRscInputs
                device={selectedDevice?.device_type_code}
                array_name={selectedDevice?.array_name}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
            </Row>) : null}
          {selectedRows.length < 2 ? (
            <ThreeParInputs
              device={selectedDevice?.device_type_code}
              port={selectedDevice?.port}
              handleInputChange={handleInputChange}
              lgColSize={6}
            />
          ) : null}
          {selectedRows.length < 2 && selectedDevice?.device_type_code?.includes?.("HITACHI") ? (
            <Row>
              <HitachiInputs
                device={selectedDevice}
                cli_path={selectedDevice?.cli_path}
                port={selectedDevice?.port}
                api_token={selectedDevice?.api_token}
                handleInputChange={handleInputChange}
                lgColSize={6}
              />
              {selectedDevice?.hitachi_ops ? (
                <Col sm={6} xs={12}>
                  <Label htmlFor="hitachi_ops">Hitachi Ops Type</Label>
                  <Input type="text" name="hitachi_ops"
                         value={selectedDevice?.hitachi_ops} disabled />
                </Col>
              ) : null}
            </Row>) : null}
          {selectedDevice?.device_type_code && selectedRows.length < 2 && selectedDevice?.device_type_code?.includes?.("VMAX") ? (
            <Row>
              <Col sm={6} xs={12}>
                <Label htmlFor="serial_number">Serial Number</Label>
                <Input required type="text" name="serial_number"
                       value={selectedDevice?.serial_number}
                       onChange={handleInputChange} />
              </Col>
            </Row>
          ) : null}
          {selectedRows.length < 2 && selectedDevice?.device_type_code?.includes?.("POWERMAX_DEVICE") ? (
            <Row>
              <Col sm={6} xs={12}>
                <Label htmlFor="serial_number">Serial Number</Label>
                <Input required type="text" name="serial_number"
                       value={selectedDevice?.serial_number}
                       onChange={handleInputChange} />
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col>
              <Label htmlFor="report_rtm_data">Enable interval reporting?</Label>
                  <Input
                    className={"ms-2"}
                    defaultChecked={selectedDevice?.report_rtm_data ?? false}
                    value={selectedDevice?.report_rtm_data ?? false}
                    name="report_rtm_data"
                    type="checkbox"
                    onChange={handleInputChange}
                  />
            </Col>
          </Row>
          {selectedRows.length < 2 ? (
            <Row>
              <Col xs={12}>
                <Button className="mb-4 mt-4"
                        color="secondary"
                        type="button"
                        onClick={changeIpBtn}>
                  {changeIp ? "Cancel Changes" : "Change IP Address/DNS Entry?"}
                </Button>
              </Col>
            </Row>
          ) : null}
          {changeIp && selectedRows.length < 2 ? (
            <Row>
              <Col sm={12} xs={12}>
                <Label htmlFor="ip_address">IP Address/DNS Entry Name
                  {selectedDevice?.ip_address !== null && !(validateIp(selectedDevice?.ip_address)) ?
                    <span className="badge bg-danger"> Invalid IP Address</span> : null}
                </Label>
                <Input required type="text"
                       pattern=""
                       value={selectedDevice?.ip_address}
                       name="ip_address" onChange={handleInputChange} />

              </Col>
              {
                (selectedDevice?.device_type_code?.includes?.("INFINIDAT_DEVICE")) ? (
                  <Col sm={12} xs={12}>
                    <Label htmlFor="secondary_ip">InfiniMetrics IP
                      {selectedDevice?.ip_address !== null && !(validateIp(selectedDevice?.secondary_ip)) ?
                        <span className="badge bg-danger"> Invalid IP Address</span> : null}
                      {}
                    </Label>
                    <Input type="text" value={selectedDevice?.secondary_ip}
                           name="secondary_ip" onChange={handleInputChange} />
                  </Col>
                ) : null}
            </Row>
          ) : null}

          {selectedRows.length < 2 ?
            <Row>
              <Col xs={12}>
                <Button className="mb-4" color="secondary" type="button"
                        onClick={changePasswordBtn}>
                  {changePassword ? "Cancel Changes" : `Change ${Nouns.TokenInVaultDevices.includes(selectedDevice?.device_type_code?.trim?.().toUpperCase?.()) ? "API Token" : "Password"}?`}
                </Button>
              </Col>
            </Row> : null}


          {!Nouns.TokenInVaultDevices.includes(selectedDevice?.device_type_code?.trim?.().toUpperCase?.()) && changePassword && selectedRows.length < 2 ? (
            <>
              <Row>
                <Col lg={6} sm={6} xs={12}>
                  <FormGroup>
                    <Label htmlFor="password">
                      {usePassword ? "Password" : "SSH Key"} (click {" "}
                      <a onClick={handleToggleUsePasword} className="custom-link">here</a>
                      {" "} to use {usePassword ? "ssh key" : "password"})
                    </Label>
                    {usePassword ? (
                      <Input required type="password" name="password" autoComplete="off"
                             value={selectedDevice?.password} onChange={handleInputChange} />
                    ) : (
                      <Input required type="textarea" name="ssh_key"
                             value={selectedDevice?.ssh_key} onChange={handleInputChange} />
                    )}
                  </FormGroup>
                </Col>
                {usePassword ? (
                  <Col lg={6} sm={6} xs={12}>
                    <FormGroup>
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input required type="password" value={selectedDevice?.confirmPassword}
                             name="confirmPassword" onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                ) : null}
              </Row>
              {usePassword && (selectedDevice?.password !== selectedDevice?.confirmPassword) ? (
                <Row>
                  <Col lg={12} sm={12} xs={12}>
                    <span className="badge bg-danger">Passwords do not match!</span>
                  </Col>
                </Row>
              ) : null}
            </>
          ) : Nouns.TokenInVaultDevices.includes(selectedDevice?.device_type_code?.trim?.().toUpperCase?.()) && changePassword && selectedRows.length < 2 ? (
            <Row>
              <Label htmlFor="password">Insert new API Token:</Label>
              <InputGroup>
                <Input
                  required
                  type={"password"}
                  name="password"
                  value={selectedDevice?.password}
                  onChange={(event) => {
                    let { value } = event.target;
                    setSelectedDevice({
                      ...selectedDevice,
                      password: value,
                      confirmPassword: value
                    });
                  }}
                />
              </InputGroup>
            </Row>
          ) : null}

          {selectedRows.length >= 2 ? (

            <>
              <Row>
                <Col lg={6} sm={6} xs={12}>
                  <FormGroup>
                    <Label htmlFor="password">
                      Password
                    </Label>
                    <Input required type="password" name="password" autoComplete="off"
                           value={selectedDevice?.password} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col lg={6} sm={6} xs={12}>
                  <FormGroup>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input required type="password" value={selectedDevice?.confirmPassword}
                           name="confirmPassword" onChange={handleInputChange} />
                  </FormGroup>
                </Col>
              </Row>
              {(selectedDevice?.password !== selectedDevice?.confirmPassword) ? (
                <Row>
                  <Col lg={12} sm={12} xs={12}>
                    <span className="badge bg-danger">Passwords do not match!</span>
                  </Col>
                </Row>
              ) : null}
            </>
          ) : null}

          <Modal className="delete-modal" backdrop="static"
                 isOpen={showDeleteModal}>
            <ModalHeader className={`bg-${theme}`} toggle={toggleDeleteModal}>
              <VsiSpanText>Are you sure you want to
                delete this device?</VsiSpanText>
            </ModalHeader>
            <ModalFooter className={`bg-${theme}`}>
              <Button color="danger" onClick={handleDeviceDelete}>Yes</Button>
              <Button color="secondary"
                      onClick={toggleDeleteModal}>No</Button>
            </ModalFooter>
          </Modal>

          <Modal className={`warning-modal bg-${theme}`} backdrop="static"
                 isOpen={showWarningModal}>
            <ModalHeader toggle={toggleWarningModal} className={`bg-${theme}`}>
              <VsiSpanText>Password and confirm password do not match.</VsiSpanText>
            </ModalHeader>
            <ModalFooter className={`bg-${theme}`}>
              <Button onClick={toggleWarningModal} color="secondary">Ok</Button>
            </ModalFooter>
          </Modal>

        </ModalBody>
        <ModalFooter className={`bg-${theme}`}>
          <Button type="submit" color="success" disabled={!validateForm()} onClick={updateDevicesHandler}>Update
            Device{selectedRows.length > 1 ? "s" : ""}</Button>

          {selectedRows.length < 2 ?
            <Button color="danger" onClick={handleDeviceDelete}>Delete Device</Button> : null}
          <Button color="secondary" onClick={toggleDeviceModal}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Alert
        color={alertColor}
        isOpen={alertVisible}
        className="text-center"
        toggle={() => setAlertVisible(false)}
      >
        {alertMessage}
      </Alert>

      <Modal className={`info-modal bg-${theme}`} backdrop="static" size="lg" isOpen={showUploadsModal}
             fullscreen={true}>
        <ModalHeader className={`bg-${theme}`} toggle={toggleUploadsModal}>
          <VsiSpanText>Latest Uploads</VsiSpanText>
        </ModalHeader>
        <ModalBody className={`bg-${theme}`}>
          <Grid data={uploads?.slice?.(0, 20) ?? []} columnDefs={uploadDefs} />
        </ModalBody>
        <ModalFooter className={`bg-${theme}`}>
          <Button onClick={toggleUploadsModal} color={"primary"}>Ok</Button>
        </ModalFooter>
      </Modal>

      <Modal size="lg" className="file-analysis-modal" backdrop="static" isOpen={showFileAnalysisModal}>
        <ModalHeader className={`bg-${theme}`} toggle={toggleFileAnalysisModal}>
          <VsiSpanText>
            <b>Edit File Analysis Device</b>
          </VsiSpanText>
        </ModalHeader>
        <ModalBody className={`bg-${theme}`}>
          <Row>
            <Col sm={6} lg={6}>
              <Label htmlFor="cli_path">Share Path</Label>
              <Input type="text" value={selectedDevice?.cli_path}
                     name="cli_path" onChange={handleInputChange} />
            </Col>
          </Row>
          <Row>
            {(changeCollectionMachine || selectedRows.length > 1) ?
              <Col sm={6} lg={6}>
                <Label htmlFor="site_code">Collection Machine (Optional)</Label>
                <Input type="text" name="site_code" value={selectedDevice?.site_code}
                       onChange={handleInputChange} />
              </Col> : null}
            {selectedRows.length < 2 ?
              <Col sm={6} xs={6}
                   style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                {CollectionMachineLabel(changeCollectionMachine)}
              </Col> : null}
          </Row>
          <Row>
            <Col lg={6} sm={6}>
              <Label htmlFor="device_alias">Device Alias (Optional)</Label>
              <Input type="text" name="device_alias" value={selectedDevice?.device_alias}
                     onChange={handleInputChange} />
            </Col>
            <Col sm={6} lg={6}>
              <Label htmlFor="collectionDays">Collection Day(s)
                {selectedDevice?.collectionDays?.length === 0 && (
                  <span className="badge bg-danger"> Required </span>
                )}
              </Label>
              <VsiLabeledSelect
                defaultValue={selectedDays}
                minWidth={"100%"}
                options={[
                  { value: "mon", label: "Monday" },
                  { value: "tue", label: "Tuesday" },
                  { value: "wed", label: "Wednesday" },
                  { value: "thurs", label: "Thursday" },
                  { value: "fri", label: "Friday" },
                  { value: "sat", label: "Saturday" },
                  { value: "sun", label: "Sunday" }
                ]} name="collection_day"
                isMulti
                onChange={handleMultiSelectChange}
              />
            </Col>

            <Col sm={3} lg={3}>
              <Label htmlFor="collection_time">Collection Time</Label>
              <Input
                id={"collection_time"}
                type="time"
                bsSize="large"
                required={false}
                name="collection_time"
                value={selectedDevice?.collection_time}
                className={"form-control"}
                onChange={handleInputChange}
                step={300}
              />
            </Col>

            <Col sm={3} lg={3}>
              <Label htmlFor="timeout">Collection Timeout (hrs)</Label>
              <Input value={selectedDevice?.timeout}
                     name="timeout"
                     type="number"
                     id={`device-timeout`}
                     onChange={handleInputChange} />
            </Col>
            <Col sm={3} lg={3}>
              <Label htmlFor="device-enabled" style={{ marginRight: 10 }}>Enabled</Label><br />
              <Toggle id="device-enabled" name="enabled"
                      checked={selectedDevice?.enabled}
                      onChange={() => {
                        setSelectedDevice({ ...selectedDevice, enabled: !selectedDevice?.enabled });
                      }} />
            </Col>

          </Row>

        </ModalBody>
        <ModalFooter className={`bg-${theme}`}>
          <Button color="success" onClick={updateDevicesHandler}>Update Device</Button>
          <Button color="danger" onClick={handleDeviceDelete}>Delete Device</Button>
          <Button color="secondary" onClick={toggleFileAnalysisModal}>Cancel</Button>
        </ModalFooter>
      </Modal>


    </div>
  );
}
